import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FileText, ArrowLeft, ExternalLink } from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  DRAFT: { bg: '#F3F4F6', color: '#6B7280' },
  READY: { bg: '#D1FAE5', color: '#059669' },
  DEPLOYED: { bg: '#D1FAE5', color: '#059669' },
  ACTIVE: { bg: '#DBEAFE', color: '#2563EB' },
};

export default function CreatorContractsUsedPage() {
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/contracts/drafts`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setDrafts(res.data?.drafts || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="creator-contracts-used-page" style={{ padding: '40px', maxWidth: '960px', margin: '0 auto' }}>
      <button data-testid="used-back-btn" onClick={() => navigate('/creator/billing')}
        style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.primary, fontSize: '14px', fontWeight: 600, marginBottom: '24px' }}>
        <ArrowLeft size={16} /> Back to Billing
      </button>

      <h1 style={{ fontSize: '28px', fontWeight: 700, color: theme.colors.text.primary, fontFamily: theme.fonts.heading, marginBottom: '6px' }}>
        Used Contracts
      </h1>
      <p style={{ fontSize: '15px', color: theme.colors.text.secondary, marginBottom: '24px' }}>
        Contract drafts you have created — each one counts against your quota.
      </p>

      {loading ? (
        <div style={{ padding: '40px', color: theme.colors.text.muted }}>Loading...</div>
      ) : drafts.length === 0 ? (
        <div style={{ background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.card, padding: '48px', textAlign: 'center' }}>
          <FileText size={40} color={theme.colors.text.muted} style={{ marginBottom: '10px' }} />
          <p style={{ color: theme.colors.text.muted, fontSize: '14px' }}>No contract drafts yet.</p>
        </div>
      ) : (
        <div data-testid="used-list" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {drafts.map(d => {
            const st = STATUS_STYLES[d.status] || STATUS_STYLES.DRAFT;
            const isDeployed = !!(d.deployed || d.contract_address);
            return (
              <div
                key={d.id}
                data-testid={`used-row-${d.id}`}
                onClick={() => navigate(isDeployed ? `/creator/contracts/${d.id}/deploy` : `/creator/contracts/drafts/${d.id}`)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '16px 20px', background: theme.colors.bg.card,
                  border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.md,
                  cursor: 'pointer', transition: 'box-shadow 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = theme.colors.shadows.sm}
                onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <FileText size={18} color={theme.colors.primary} />
                  <div>
                    <p style={{ fontSize: '15px', fontWeight: 600, color: theme.colors.text.primary, margin: 0 }}>
                      {d.params?.contract_name || d.template_name || 'Untitled Contract'}
                    </p>
                    <p style={{ fontSize: '12px', color: theme.colors.text.muted, margin: '2px 0 0' }}>
                      {d.standard} · {d.template_name} · {new Date(d.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 700, background: st.bg, color: st.color }}>
                    {d.status}
                  </span>
                  {isDeployed && <ExternalLink size={14} color={theme.colors.text.muted} />}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
